import "draft-js/dist/Draft.css";
import "./styles.css";

import { forwardRef, KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    ButtonProps,
    Divider,
    Flex,
    Group,
    Menu,
    Paper,
    Popover,
    Slider,
    Text,
    TextInput,
    Title,
    Tooltip,
    useMantineColorScheme,
    useMantineTheme,
} from "@mantine/core";
import {
    IconAlignCenter,
    IconAlignJustified,
    IconAlignLeft,
    IconAlignRight,
    IconArrowBackUp,
    IconArrowForwardUp,
    IconBold,
    IconH1,
    IconH2,
    IconH3,
    IconH4,
    IconH5,
    IconH6,
    IconHeading,
    IconItalic,
    IconLinkPlus,
    IconList,
    IconListNumbers,
    IconPhotoPlus,
    IconUnderline,
    TablerIconsProps,
} from "@tabler/icons-react";
import {
    AtomicBlockUtils,
    CompositeDecorator,
    ContentBlock,
    ContentState,
    convertFromRaw,
    convertToRaw,
    Editor,
    EditorCommand,
    EditorState,
    getDefaultKeyBinding,
    Modifier,
    RichUtils,
} from "draft-js";
import { getSelectionInlineStyle } from "draftjs-utils";
import * as Immutable from "immutable";
import punycode from "punycode";
import { File as CDNFile, RichText } from "src/graphql/generated/graphql";

import { useFileInput } from "../../hooks/fileInput";

const decodeUnicode = (str: string): number[] => punycode?.ucs2?.decode?.(str);

const buttonIconProps: TablerIconsProps = { size: 16 };
const buttonProps: ButtonProps = {
    size: "xs",
    color: "dark",
    variant: "default",
} as const;

const inlineStyles = {
    BOLD: { tooltipLabel: "bold", icon: IconBold },
    ITALIC: { tooltipLabel: "italic", icon: IconItalic },
    UNDERLINE: { tooltipLabel: "underline", icon: IconUnderline },
    // CODE:      { tooltipLabel: "monospace", icon: IconLetterSpacing },
} as const;

const customStyleMap = {
    FONT_SIZE_10: {
        fontSize: "10px",
        lineHeight: "22px",
    },
    FONT_SIZE_12: {
        fontSize: "12px",
        lineHeight: "22px",
    },
    FONT_SIZE_14: {
        fontSize: "14px",
        lineHeight: "28px",
    },
    FONT_SIZE_16: {
        fontSize: "16px",
        lineHeight: "28px",
    },
    FONT_SIZE_18: {
        fontSize: "18px",
        lineHeight: "28px",
    },
    FONT_SIZE_20: {
        fontSize: "20px",
        lineHeight: "32px",
    },
    FONT_SIZE_22: {
        fontSize: "22px",
        lineHeight: "32px",
    },
} as const;

const defaultFontSize = "FONT_SIZE_16";

const fontSizes = {
    FONT_SIZE_10: {
        sizePx: 10,
    },
    FONT_SIZE_12: {
        sizePx: 12,
    },
    FONT_SIZE_14: {
        sizePx: 14,
    },
    FONT_SIZE_16: {
        sizePx: 16,
    },
    FONT_SIZE_18: {
        sizePx: 18,
    },
    FONT_SIZE_20: {
        sizePx: 20,
    },
    FONT_SIZE_22: {
        sizePx: 22,
    },
} as const;

const headers = {
    "header-one": { order: 1, icon: IconH1 },
    "header-two": { order: 2, icon: IconH2 },
    "header-three": { order: 3, icon: IconH3 },
    "header-four": { order: 4, icon: IconH4 },
    "header-five": { order: 5, icon: IconH5 },
    "header-six": { order: 6, icon: IconH6 },
} as const;

const blockStyles = {
    "unordered-list-item": { tooltipLabel: "unorderedlist", icon: IconList },
    "ordered-list-item": { tooltipLabel: "orderedlist", icon: IconListNumbers },
    // "code-block":          { tooltipLabel: "code", icon: IconCode },
    // "blockquote":          { tooltipLabel: "blockquote", icon: IconBlockquote },
} as const;

const textAlignments = {
    left: { tooltipLabel: "alignleft", icon: IconAlignLeft },
    center: { tooltipLabel: "aligncenter", icon: IconAlignCenter },
    right: { tooltipLabel: "alignright", icon: IconAlignRight },
    justify: { tooltipLabel: "alignjustify", icon: IconAlignJustified },
} as const;

const blockStyleFn = (contentBlock: ContentBlock): string => {
    const prefix = "richtext-block";
    const classNames = [prefix];

    const blockType = contentBlock.getType();
    const blockData = contentBlock.getData();

    classNames.push(`${prefix}-${blockType}`);

    const textAlign = blockData.get("text-align");
    if (textAlign) {
        classNames.push(`${prefix}-align-${textAlign}`);
    }

    return classNames.join(" ");
};

const blockRendererFn = (block: ContentBlock, { }: { getEditorState: () => EditorState }) => {
    switch (block.getType()) {
        case "atomic":
            return {
                component: Atomic,
                editable: false,
            };
    }

    return null;
};

interface AtomicProps {
    contentState: ContentState;
    block: ContentBlock;
}

const Atomic = forwardRef(({ contentState, block }: AtomicProps, ref) => {
    const entityKey = block.getEntityAt(0);
    const entity = contentState.getEntity(entityKey);

    switch (entity?.getType?.().toString().toUpperCase()) {
        case "IMAGE":
            return <Image contentState={contentState} entityKey={entityKey} ref={ref} />;
    }

    return null;
});

const Link = (props) => {
    const { contentState, entityKey } = props;
    const data = contentState.getEntity(entityKey).getData();

    const theme = useMantineTheme();

    return (
        <a
            // rel="noopener noreferrer"
            target="_blank"
            className={`richtext-link ${data.className || ""}`}
            style={{ color: theme.primaryColor }}
            href={data.href}
            aria-label={data.text || data.href}
        >
            {props?.children || data.text}
        </a>
    );
};

const Image = (props) => {
    const { contentState, entityKey } = props;
    const data = contentState.getEntity(entityKey).getData();

    return <img {...data} ref={props?.ref} className={`richtext-image ${data.className || ""}`} />;
};

const decorator = new CompositeDecorator([
    {
        component: Link,
        strategy: (contentBlock, callback, contentState) => {
            contentBlock.findEntityRanges((character) => {
                const entityKey = character.getEntity();

                return !!entityKey && contentState.getEntity(entityKey).getType() === "LINK";
            }, callback);
        },
    },
    {
        component: Image,
        strategy: (contentBlock, callback, contentState) => {
            contentBlock.findEntityRanges((character) => {
                const entityKey = character.getEntity();

                return !!entityKey && contentState.getEntity(entityKey).getType() === "IMAGE";
            }, callback);
        },
    },
]);

const getBlockMapKeys = (
    contentState: ContentState,
    startKey: string,
    endKey: string
): Immutable.Iterable<number, string> => {
    const blockMapKeys = contentState.getBlockMap().keySeq();

    return blockMapKeys
        .skipUntil((key) => key === startKey)
        .takeUntil((key) => key === endKey)
        .concat([endKey]);
};

const getSelectedBlocksMapKeys = (editorState: EditorState): Immutable.Iterable<number, string> => {
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    return getBlockMapKeys(contentState, selection.getStartKey(), selection.getEndKey());
};

const isBlockInSelection = (editorState: EditorState, blockKey: string): boolean => {
    return getSelectedBlocksMapKeys(editorState).includes(blockKey);
};

export interface RichTextEditorProps {
    value: RichText;
    valueUpdateKey?: string;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    onChange?(value: RichText);
    onImageUpload?(file: File): Promise<CDNFile>;
}

export const RichTextEditor = ({
    value,
    valueUpdateKey,
    placeholder,
    disabled,
    readOnly,
    onChange,
    onImageUpload,
}: RichTextEditorProps) => {
    const { t } = useTranslation(["common"]);
    const { colorScheme } = useMantineColorScheme();

    const _buttonProps = { ...buttonProps, disabled };

    const editorRef = useRef<Editor>();

    const [hasFocus, setHasFocus] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    const [lastValueUpdateKey, setLastValueUpdateKey] = useState(valueUpdateKey);

    const [editorState, setEditorState] = useState(
        /^\{.*\}$/.test(value?.json)
            ? EditorState.createWithContent(convertFromRaw(JSON.parse(value.json)), decorator)
            : EditorState.createEmpty(decorator)
    );

    useEffect(() => {
        if (!isEdited || lastValueUpdateKey !== valueUpdateKey) {
            setLastValueUpdateKey(valueUpdateKey);
            setEditorState(
                /^\{.*\}$/.test(value?.json)
                    ? EditorState.createWithContent(convertFromRaw(JSON.parse(value.json)), decorator)
                    : EditorState.createEmpty(decorator)
            );
        }
    }, [isEdited, value, valueUpdateKey, lastValueUpdateKey]);

    const getEditorState = useCallback(() => {
        return editorState;
    }, [editorState]);

    const hasStyledTextWithoutText = useMemo(() => {
        const contentState = editorState.getCurrentContent();
        return !contentState.hasText() && contentState.getBlockMap().first().getType() !== "unstyled";
    }, [editorState]);

    const { selectedInlineStyle, selectedFontSize } = useMemo((): {
        selectedFontSize: string;
        selectedInlineStyle: {
            BOLD: boolean;
            CODE: boolean;
            ITALIC: boolean;
            STRIKETHROUGH: boolean;
            SUBSCRIPT: boolean;
            SUPERSCRIPT: boolean;
            UNDERLINE: boolean;
        };
    } => {
        const selectedInlineStyle = getSelectionInlineStyle(editorState);

        const currentInlineStyle = editorState.getCurrentInlineStyle().toArray();

        let selectedFontSize = defaultFontSize;
        for (let index = (currentInlineStyle || []).length - 1; index >= 0; index -= 1) {
            if (Object.keys(fontSizes).indexOf(currentInlineStyle[index]) >= 0) {
                selectedFontSize = currentInlineStyle[index];
                break;
            }
        }

        return { selectedInlineStyle, selectedFontSize };
    }, [editorState]);

    const { selectedBlock, selectedEntityKey, selectedEntity } = useMemo(() => {
        const contentState = editorState.getCurrentContent();
        const startKey = editorState.getSelection().getStartKey();
        const selectedBlock = contentState.getBlockForKey(startKey);

        const selectedEntityKey = selectedBlock.getEntityAt(editorState.getSelection().getStartOffset());
        let selectedEntity: Draft.DraftModel.Entity.DraftEntityInstance;

        if (selectedEntityKey) {
            selectedEntity = contentState.getEntity(selectedEntityKey);
        }

        return {
            selectedBlock,
            selectedEntityKey,
            selectedEntity,
        };
    }, [editorState]);

    const selectedHeader = useMemo(() => {
        const header = headers[selectedBlock.getType()] || null;

        if (!header) {
            return null;
        }

        return {
            ...header,
            key: selectedBlock.getType(),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBlock]);

    const [wpm, setWpm] = useState(183);

    const { linesCount, wordsCount, charactersCount } = useMemo(() => {
        const plainText = editorState.getCurrentContent().getPlainText("");

        const blocksArray = editorState.getCurrentContent().getBlocksAsArray();
        const wordsArray = plainText
            .replace(/(?:\r\n|\r|\n)/g, " ")
            .trim()
            .match(/\S+/g);

        return {
            linesCount: blocksArray ? blocksArray.length : null,
            wordsCount: wordsArray ? wordsArray.length : 0,
            charactersCount: decodeUnicode(plainText.replace(/(?:\r\n|\r|\n)/g, "").trim()).length,
        };
    }, [editorState]);

    const estimatedReadingTimeMinutes = useMemo(() => {
        if (!wordsCount || !wpm) {
            return 0;
        }

        return wordsCount / wpm;
    }, [wordsCount, wpm]);

    const handleEditorStateChange = useCallback(
        (editorState: EditorState) => {
            setEditorState(editorState);
            setIsEdited(true);

            onChange?.({ json: JSON.stringify(convertToRaw(editorState.getCurrentContent())) });
        },
        [onChange]
    );

    const focusEditor = useCallback(() => {
        if (editorRef?.current) {
            editorRef.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorRef?.current]);

    const handleKeyBinding = useCallback(
        (event: KeyboardEvent) => {
            // if (event.key === "ArrowLeft") {
            //     const selection = editorState.getSelection();
            //     const selectionKey = selection.getAnchorKey();
            //     const beforeBlock = editorState
            //         .getCurrentContent()
            //         .getBlockBefore(selectionKey);

            //     // if (
            //     //     beforeBlock &&
            //     //     selection.getAnchorOffset() === 0 &&
            //     //     blockKeyStore.includes(beforeBlock.getKey())
            //     // ) {
            //     //     setSelection(getEditorState, setEditorState, 'up', evt);
            //     // }
            // }

            // if (event.key === "ArrowRight") {
            //     const selection = editorState.getSelection();
            //     const selectionKey = selection.getFocusKey();
            //     const currentBlock = editorState
            //         .getCurrentContent()
            //         .getBlockForKey(selectionKey);

            //     const afterBlock = editorState
            //         .getCurrentContent()
            //         .getBlockAfter(selectionKey);

            //     const notAtomicAndLastPost =
            //         currentBlock.getType() !== "atomic" &&
            //         currentBlock.getLength() === selection.getFocusOffset();

            //     if (
            //         afterBlock
            //         && currentBlock?.getType?.()?.toString()?.toUpperCase?.() !== "ATOMIC"
            //         && afterBlock?.getType?.()?.toString()?.toUpperCase?.() === "ATOMIC"
            //         && editorState.getCurrentContent().getEntity(afterBlock?.getEntityAt?.(0))?.getType?.()?.toString()?.toUpperCase?.() === "IMAGE"
            //     ) {
            //         alert("Image!!")
            //     }

            //     // if (
            //     //     afterBlock &&
            //     //     notAtomicAndLastPost &&
            //     //     blockKeyStore.includes(afterBlock.getKey())
            //     // ) {
            //     //     setSelection(getEditorState, setEditorState, 'down', evt);
            //     // }
            // }

            if (event.key === "Backspace") {
                const selection = editorState.getSelection();
                const startKey = selection.getStartKey();

                if (isBlockInSelection(editorState, startKey)) {
                    const block = editorState.getCurrentContent().getBlockForKey(startKey);

                    if (block.getType() === "atomic") {
                        const blockMapKeys = getSelectedBlocksMapKeys(editorState);

                        const newContentState = blockMapKeys.reduce((contentState, key) => {
                            return Modifier.removeRange(contentState, editorState.getSelection(), "backward");
                        }, editorState.getCurrentContent());

                        handleEditorStateChange(EditorState.push(editorState, newContentState, "remove-range"));
                        return "handled";
                    }
                }

                if (selectedEntityKey) {
                    handleEditorStateChange(RichUtils.toggleLink(editorState, selection, null));
                    return "handled";
                }
            }
            return getDefaultKeyBinding(event);
        },
        [editorState, handleEditorStateChange, selectedEntityKey]
    );

    const handleKeyCommand = useCallback(
        (command: EditorCommand, editorState: EditorState) => {
            const newEditorState = RichUtils.handleKeyCommand(editorState, command);

            if (newEditorState) {
                handleEditorStateChange(newEditorState);
                return "handled";
            }

            return "not-handled";
        },
        [handleEditorStateChange]
    );

    const handleUndo = useCallback(() => {
        handleEditorStateChange(EditorState.undo(editorState));
    }, [editorState, handleEditorStateChange]);

    const handleRedo = useCallback(() => {
        handleEditorStateChange(EditorState.redo(editorState));
    }, [editorState, handleEditorStateChange]);

    const handleToggleInlineStyle = useCallback(
        (inlineStyle: string) => {
            return (event: MouseEvent<HTMLButtonElement>) => {
                event?.preventDefault?.();

                const newState = RichUtils.toggleInlineStyle(editorState, inlineStyle);
                handleEditorStateChange(newState);
            };
        },
        [editorState, handleEditorStateChange]
    );

    const handleToggleBlockType = useCallback(
        (blockType: string) => {
            return (event: MouseEvent<HTMLButtonElement>) => {
                event?.preventDefault?.();

                handleEditorStateChange(RichUtils.toggleBlockType(editorState, blockType));
            };
        },
        [editorState, handleEditorStateChange]
    );

    const handleToggleTextAlignment = useCallback(
        (alignment: string) => {
            return (event: MouseEvent<HTMLButtonElement>) => {
                event?.preventDefault?.();

                handleEditorStateChange(
                    EditorState.push(
                        editorState,
                        Modifier.setBlockData(
                            editorState.getCurrentContent(),
                            editorState.getSelection(),
                            Immutable.Map({ "text-align": alignment })
                        ),
                        "change-block-data"
                    )
                );
            };
        },
        [editorState, handleEditorStateChange]
    );

    const [displayLinkDialog, setDisplayLinkDialog] = useState(false);
    const [linkReplace, setLinkReplace] = useState(false);
    const [linkHref, setLinkHref] = useState<string>();
    const [linkText, setLinkText] = useState<string>();

    const isLinkSelected = selectedEntity?.getType?.() === "LINK";

    const handleLinkButtonMouseDown = useCallback(
        (event) => {
            event?.preventDefault?.();

            focusEditor();

            if (isLinkSelected) {
                const { href } = selectedEntity?.getData?.() || {};
                setLinkHref(href);
            }

            const selection = editorState.getSelection();
            const startOffset = selection.getStartOffset();
            const endOffset = selection.getEndOffset();

            if (endOffset > startOffset) {
                setLinkText(selectedBlock.getText().slice(startOffset, endOffset));
                setLinkReplace(true);
            }

            setDisplayLinkDialog(true);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [editorState, isLinkSelected, selectedBlock, selectedEntity]
    );

    const handleLinkApply = useCallback(
        (event) => {
            event?.preventDefault?.();

            focusEditor();

            const cleanup = () => {
                setDisplayLinkDialog(false);
                setLinkReplace(false);
                setLinkHref(null);
                setLinkText(null);
            };

            if (isLinkSelected) {
                const contentState = editorState.getCurrentContent();
                const newContentState = contentState.mergeEntityData(selectedEntityKey, {
                    href: linkHref,
                });

                handleEditorStateChange(EditorState.push(editorState, newContentState, "apply-entity"));
                cleanup();
                return;
            }

            // With special thanks to: https://codesandbox.io/p/sandbox/interesting-leaf-rhyx3?file=%2Fsrc%2FApp.tsx%3A11%2C17

            const contentStateWithEntity = editorState
                .getCurrentContent()
                .createEntity("LINK", "MUTABLE", { href: linkHref, text: linkText || linkHref });
            const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

            if (linkReplace) {
                const editorStateWithText = Modifier.replaceText(
                    editorState.getCurrentContent(),
                    editorState.getSelection(),
                    linkText,
                    editorState.getCurrentInlineStyle(),
                    entityKey
                );

                const newEditorState = EditorState.set(editorState, {
                    currentContent: contentStateWithEntity,
                });

                const newEditorStateWithLink = RichUtils.toggleLink(
                    newEditorState,
                    newEditorState.getSelection(),
                    entityKey
                );

                handleEditorStateChange(
                    EditorState.push(newEditorStateWithLink, editorStateWithText, "insert-characters")
                );
                cleanup();
                return;
            }

            const contentStateWithLink = Modifier.insertText(
                contentStateWithEntity,
                editorState.getSelection(),
                linkText,
                null,
                entityKey
            );

            handleEditorStateChange(EditorState.createWithContent(contentStateWithLink, decorator));
            cleanup();
            return;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [editorState, linkReplace, linkHref, linkText, isLinkSelected, selectedEntityKey]
    );

    const handleInsertImage = useCallback(
        (image: CDNFile) => {
            const contentStateWithEntity = editorState
                .getCurrentContent()
                .createEntity("IMAGE", "IMMUTABLE", { src: image?.src });

            const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

            handleEditorStateChange(
                AtomicBlockUtils.insertAtomicBlock(
                    EditorState.set(editorState, { currentContent: contentStateWithEntity }),
                    entityKey,
                    " "
                )
            );
        },
        [editorState, handleEditorStateChange]
    );

    const handleUploadImage = useCallback(
        async (file: File) => {
            const image = await onImageUpload?.(file);
            handleInsertImage(image);
        },
        [handleInsertImage, onImageUpload]
    );

    const { fileInputElement, openFileInputDialog } = useFileInput({
        onSelect: handleUploadImage,
    });

    return (
        <Paper
            component={Flex}
            style={{
                borderCollapse: "collapse",
            }}
            direction="column"
            shadow={readOnly ? "md" : undefined}
            radius={readOnly ? "md" : "sm"}
            withBorder
        >
            {fileInputElement}
            <Paper
                component={Flex}
                direction="row"
                wrap="wrap"
                radius="sm"
                style={{
                    display: readOnly ? "none" : undefined,
                }}
                sx={(theme) => ({
                    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[1],
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                })}
            >
                <Button.Group>
                    <Tooltip label={t("richtext_undo")} withinPortal>
                        <Button {..._buttonProps} onMouseDown={handleUndo}>
                            <IconArrowBackUp {...buttonIconProps} />
                        </Button>
                    </Tooltip>
                    <Tooltip label={t("richtext_redo")} withinPortal>
                        <Button {..._buttonProps} onMouseDown={handleRedo}>
                            <IconArrowForwardUp {...buttonIconProps} />
                        </Button>
                    </Tooltip>
                </Button.Group>
                <Divider mx="md" orientation="vertical" />
                <Button.Group>
                    {Object.keys(inlineStyles).map((key: keyof typeof inlineStyles, index) => {
                        const Icon = inlineStyles[key].icon;

                        return (
                            <Tooltip
                                key={`${index}${key}`}
                                label={t(`richtext_${inlineStyles[key].tooltipLabel}`)}
                                withinPortal
                            >
                                <Button
                                    {..._buttonProps}
                                    variant={
                                        selectedInlineStyle[key]
                                            ? colorScheme === "dark"
                                                ? "white"
                                                : "filled"
                                            : "default"
                                    }
                                    onMouseDown={handleToggleInlineStyle(key)}
                                >
                                    <Icon {...buttonIconProps} />
                                </Button>
                            </Tooltip>
                        );
                    })}
                    <Popover
                        width={320}
                        position="bottom"
                        shadow="md"
                        opened={displayLinkDialog}
                        onClose={() => setDisplayLinkDialog(false)}
                        withArrow
                    >
                        <Popover.Target>
                            <Tooltip
                                label={isLinkSelected ? t("richtext_link_update") : t("richtext_link_add")}
                                withinPortal
                            >
                                <Button
                                    {..._buttonProps}
                                    variant={isLinkSelected ? (colorScheme === "dark" ? "white" : "filled") : "default"}
                                    onMouseDown={handleLinkButtonMouseDown}
                                >
                                    <IconLinkPlus {...buttonIconProps} />
                                </Button>
                            </Tooltip>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <TextInput
                                placeholder="https://example.com"
                                label={t("richtext_link_url")}
                                value={linkHref || ""}
                                onChange={(event) => setLinkHref(event?.target?.value)}
                            />
                            {!isLinkSelected && (
                                <TextInput
                                    mt="xs"
                                    label={t("richtext_link_text")}
                                    value={linkText || ""}
                                    onChange={(event) => setLinkText(event?.target?.value)}
                                />
                            )}
                            <Group mt="md" position="right">
                                <Button size="sm" onClick={handleLinkApply}>
                                    {isLinkSelected ? t("richtext_link_update") : t("richtext_link_add")}
                                </Button>
                            </Group>
                        </Popover.Dropdown>
                    </Popover>
                    <Menu>
                        <Menu.Target>
                            <Tooltip label={t("richtext_fontsize")} withinPortal>
                                <Button {..._buttonProps} variant="default">
                                    {fontSizes[selectedFontSize || defaultFontSize]?.sizePx}px
                                </Button>
                            </Tooltip>
                        </Menu.Target>
                        <Menu.Dropdown>
                            {Object.keys(fontSizes).map((key, index) => {
                                return (
                                    <Menu.Item
                                        key={`${index}${key}`}
                                        w="72px"
                                        color={selectedFontSize === key ? "orange" : undefined}
                                        onMouseDown={handleToggleInlineStyle(key)}
                                    >
                                        <Text size="md" weight={500}>
                                            {fontSizes[key].sizePx}px
                                        </Text>
                                    </Menu.Item>
                                );
                            })}
                        </Menu.Dropdown>
                    </Menu>
                </Button.Group>
                {typeof onImageUpload === "function" && (
                    <>
                        <Divider mx="md" orientation="vertical" />
                        <Tooltip label={t("richtext_image_upload")} withinPortal>
                            <Button {..._buttonProps} variant="default" onMouseDown={openFileInputDialog}>
                                <IconPhotoPlus {...buttonIconProps} />
                            </Button>
                        </Tooltip>
                    </>
                )}
                <Divider mx="md" orientation="vertical" />
                <Button.Group>
                    <Menu>
                        <Menu.Target>
                            <Tooltip label={t("richtext_heading")} withinPortal>
                                <Button
                                    {..._buttonProps}
                                    variant={selectedHeader ? (colorScheme === "dark" ? "white" : "filled") : "default"}
                                >
                                    {selectedHeader ? (
                                        <selectedHeader.icon {...buttonIconProps} />
                                    ) : (
                                        <IconHeading {...buttonIconProps} />
                                    )}
                                </Button>
                            </Tooltip>
                        </Menu.Target>
                        <Menu.Dropdown>
                            {Object.keys(headers).map((key, index) => {
                                const Icon = headers[key].icon;

                                return (
                                    <Menu.Item
                                        key={`${index}${key}`}
                                        icon={<Icon {...buttonIconProps} />}
                                        color={headers[key].order === selectedHeader?.order ? "orange" : undefined}
                                        onClick={handleToggleBlockType(key)}
                                    >
                                        <Title order={headers[key].order}>{t("richtext_heading")}</Title>
                                    </Menu.Item>
                                );
                            })}
                        </Menu.Dropdown>
                    </Menu>
                    {Object.keys(blockStyles).map((key: keyof typeof blockStyles, index) => {
                        const Icon = blockStyles[key].icon;

                        return (
                            <Tooltip
                                key={`${index}${key}`}
                                label={t(`richtext_${blockStyles[key].tooltipLabel}`)}
                                withinPortal
                            >
                                <Button
                                    {..._buttonProps}
                                    variant={
                                        selectedBlock.getType() === key
                                            ? colorScheme === "dark"
                                                ? "white"
                                                : "filled"
                                            : "default"
                                    }
                                    onMouseDown={handleToggleBlockType(key)}
                                >
                                    <Icon {...buttonIconProps} />
                                </Button>
                            </Tooltip>
                        );
                    })}
                </Button.Group>
                <Divider mx="md" orientation="vertical" />
                <Button.Group>
                    {Object.keys(textAlignments).map((key: keyof typeof textAlignments, index) => {
                        const Icon = textAlignments[key].icon;

                        return (
                            <Tooltip
                                key={`${index}${key}`}
                                label={t(`richtext_${textAlignments[key].tooltipLabel}`)}
                                withinPortal
                            >
                                <Button
                                    {..._buttonProps}
                                    variant={
                                        selectedBlock.getData().get("text-align") === key
                                            ? colorScheme === "dark"
                                                ? "white"
                                                : "filled"
                                            : "default"
                                    }
                                    onMouseDown={handleToggleTextAlignment(key)}
                                >
                                    <Icon {...buttonIconProps} />
                                </Button>
                            </Tooltip>
                        );
                    })}
                </Button.Group>
                <Box style={{ flex: 1 }} />
                {/* <Button
                    {..._buttonProps}
                    onClick={() =>
                        ImportHTMLModal.display({
                            onHTML(html: string) {
                                const { contentBlocks, entityMap } = convertFromHTML(html);
                                const htmlEditorState = EditorState.createWithContent(
                                    ContentState.createFromBlockArray(contentBlocks, entityMap),
                                    decorator,
                                );
                                handleEditorStateChange(htmlEditorState);
                            },
                        })}
                >
                    {t("richtext_importhtml")}
                </Button> */}
            </Paper>
            <Paper
                ml={readOnly ? undefined : "-1px"}
                w={readOnly ? undefined : "calc(100% + 2px)"}
                radius="0"
                sx={(theme) => ({
                    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
                    border: readOnly ? "none" : "1px solid",
                    // borderTop: "1px solid",
                    // borderBottom: "1px solid",
                    borderColor: hasFocus
                        ? // (theme.colorScheme === "dark" ? theme.colors.blue[8] : theme.colors.blue[6]) :
                        theme.colorScheme === "dark"
                            ? theme.primaryColor
                            : theme.primaryColor
                        : theme.colorScheme === "dark"
                            ? theme.colors.dark[4]
                            : theme.colors.gray[4],
                    borderRadius: readOnly ? theme.radius.md : undefined,
                    minWidth: 0,
                    width: "100%",
                    appearance: "none",
                    position: "relative",
                    "& .DraftEditor-root": {
                        padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                        minHeight: "6rem",
                        cursor: "text",
                    },
                    "& .public-DraftEditorPlaceholder-root": {
                        display: hasStyledTextWithoutText ? "none" : undefined,
                    },
                })}
                onClick={focusEditor}
            >
                <Editor
                    ref={editorRef}
                    readOnly={readOnly}
                    placeholder={placeholder}
                    editorState={editorState}
                    blockStyleFn={blockStyleFn}
                    blockRendererFn={(contentBlock) => blockRendererFn(contentBlock, { getEditorState })}
                    customStyleMap={customStyleMap}
                    keyBindingFn={handleKeyBinding}
                    handleKeyCommand={handleKeyCommand}
                    onChange={handleEditorStateChange}
                    onFocus={() => setHasFocus(true)}
                    onBlur={() => setHasFocus(false)}
                    handleDrop={() => "handled"}
                />
            </Paper>
            <Paper
                component={Flex}
                direction="row"
                wrap="wrap"
                justify="center"
                radius="sm"
                style={{
                    display: readOnly ? "none" : undefined,
                }}
                sx={(theme) => ({
                    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[1],
                    // border: "1px solid",
                    // borderColor: theme.colorScheme === "dark" ?
                    //     theme.colors.dark[4] : theme.colors.gray[4],
                    // borderTop: "none",
                    // borderBottomLeftRadius: theme.radius.sm,
                    // borderBottomRightRadius: theme.radius.sm,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                })}
            >
                <Text fz="sm" c={colorScheme === "dark" ? "dark.0" : "gray"}>
                    {t("richtext_lines")}: {linesCount}
                </Text>
                <Divider mx="md" orientation="vertical" />
                <Text fz="sm" c={colorScheme === "dark" ? "dark.0" : "gray"}>
                    {t("richtext_words")}: {wordsCount}
                </Text>
                <Divider mx="md" orientation="vertical" />
                <Text fz="sm" c={colorScheme === "dark" ? "dark.0" : "gray"}>
                    {t("richtext_characters")}: {charactersCount}
                </Text>
                <Divider mx="md" orientation="vertical" />
                <Text fz="sm" c={colorScheme === "dark" ? "dark.0" : "gray"}>
                    {t("richtext_estreadtime")}: {Math.ceil(estimatedReadingTimeMinutes)}m (
                    {[
                        t("richtext_estreadtime_with"),
                        " ",
                        wpm,
                        " ",
                        <Popover key="wpm" middlewares={{ flip: true, shift: true, inline: true }} position="top">
                            <Popover.Target>
                                <Text display="inline" style={{ cursor: "pointer" }} underline>
                                    {t("richtext_estreadtime_wpm")}
                                </Text>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <Slider
                                    w="220px"
                                    min={80}
                                    max={260}
                                    step={1}
                                    label={(wpm) => `${wpm} ${t("richtext_estreadtime_wpm")}`}
                                    value={wpm}
                                    onChange={(wpm) => setWpm(wpm)}
                                />
                            </Popover.Dropdown>
                        </Popover>,
                    ]}
                    )
                </Text>
            </Paper>
        </Paper>
    );
};
